import { useState, useRef, useEffect, useCallback } from 'react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { parseBarcode, type BarcodeScanResult } from '@/hooks/useBarcodeScanner';
import { Loader2 } from 'lucide-react';

interface BarcodeInputProps {
  onScan: (result: BarcodeScanResult) => void;
  onError?: (error: string) => void;
  placeholder?: string;
  className?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  // Tiempo máximo entre caracteres para considerar como scanner USB (ms)
  scannerThreshold?: number;
  // Si es true, el input se oculta visualmente pero sigue funcional
  hidden?: boolean;
}

/**
 * Input especializado para detectar entrada de scanner USB
 * Los scanners USB envían caracteres muy rápido (<50ms entre caracteres)
 * y terminan con Enter
 */
export function BarcodeInput({
  onScan,
  onError,
  placeholder = 'Escanear código...',
  className,
  autoFocus = false,
  disabled = false,
  scannerThreshold = 50,
  hidden = false,
}: BarcodeInputProps) {
  const [isScanning, setIsScanning] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Para detección de scanner USB
  const lastKeyTime = useRef<number>(0);
  const keyBuffer = useRef<string[]>([]);
  const isUsbScanner = useRef<boolean>(false);

  // Resetear el buffer
  const resetBuffer = useCallback(() => {
    keyBuffer.current = [];
    isUsbScanner.current = false;
    setIsScanning(false);
  }, []);

  // Procesar el código escaneado
  const processScannedCode = useCallback((code: string) => {
    const result = parseBarcode(code);
    if (result) {
      onScan(result);
    } else {
      onError?.('Código de barras inválido');
    }
    setInputValue('');
    resetBuffer();
  }, [onScan, onError, resetBuffer]);

  // Manejar keydown para detectar scanner
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    const now = Date.now();
    const timeSinceLastKey = now - lastKeyTime.current;

    // Si el tiempo entre teclas es muy corto, probablemente es un scanner
    if (timeSinceLastKey < scannerThreshold && keyBuffer.current.length > 0) {
      isUsbScanner.current = true;
      setIsScanning(true);
    }

    // Si se presiona Enter y hay datos en el buffer
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation(); // Evita que el Enter cierre diálogos padre
      const scannedCode = keyBuffer.current.join('');

      if (scannedCode.length > 0) {
        processScannedCode(scannedCode);
      } else if (inputValue.trim().length > 0) {
        // Entrada manual
        processScannedCode(inputValue.trim());
      }
      return;
    }

    // Solo agregar caracteres imprimibles al buffer
    if (e.key.length === 1) {
      keyBuffer.current.push(e.key);
      lastKeyTime.current = now;
    }
  }, [inputValue, processScannedCode, scannerThreshold]);

  // Manejar cambio de input (entrada manual)
  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  }, []);

  // Limpiar buffer si pasa mucho tiempo sin teclas
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      if (now - lastKeyTime.current > 500 && keyBuffer.current.length > 0) {
        // Si hay datos en el buffer y no es un scanner, podría ser entrada manual lenta
        if (!isUsbScanner.current && keyBuffer.current.length > 0) {
          // Sincronizar con el input value
          const bufferedValue = keyBuffer.current.join('');
          if (bufferedValue !== inputValue) {
            keyBuffer.current = [];
          }
        }
        setIsScanning(false);
      }
    }, 200);

    return () => clearInterval(interval);
  }, [inputValue]);

  // AutoFocus si está habilitado
  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  // Modo hidden: captura a nivel de document. El input oculto (sr-only,
  // tabIndex=-1) nunca recibe el foco, así que su onKeyDown de React no se
  // dispararía jamás; sin esto, el escaneo con un diálogo abierto no llega
  // a ningún sitio (el listener global se inhibe con diálogos abiertos).
  useEffect(() => {
    if (!hidden || disabled) return;

    const buffer: string[] = [];
    let lastTime = 0;

    const isEditableFocused = () => {
      const el = document.activeElement as HTMLElement | null;
      return !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable);
    };

    const onDocKeyDown = (e: KeyboardEvent) => {
      // Con el cursor en un campo editable, la lectura entra ahí de forma nativa
      if (isEditableFocused()) return;

      const now = Date.now();
      // Ritmo lento = tecleo humano o lecturas separadas: reiniciar buffer
      if (now - lastTime > 100) buffer.length = 0;

      if (e.key === 'Enter') {
        if (buffer.length >= 3) {
          e.preventDefault();
          e.stopPropagation();
          processScannedCode(buffer.join(''));
        }
        buffer.length = 0;
        return;
      }

      if (e.key.length === 1) {
        buffer.push(e.key);
        lastTime = now;
      }
    };

    // Fase de captura: se ejecuta antes que cualquier otro listener de la app
    document.addEventListener('keydown', onDocKeyDown, true);
    return () => document.removeEventListener('keydown', onDocKeyDown, true);
  }, [hidden, disabled, processScannedCode]);

  if (hidden) {
    return (
      <input
        ref={inputRef}
        type="text"
        value={inputValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        className="sr-only"
        tabIndex={-1}
        aria-hidden="true"
        disabled={disabled}
      />
    );
  }

  return (
    <div className="relative">
      <Input
        ref={inputRef}
        type="text"
        value={inputValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        className={cn(
          'pr-8',
          isScanning && 'bg-primary/5 border-primary',
          className
        )}
      />
      {isScanning && (
        <div className="absolute right-2 top-1/2 -translate-y-1/2">
          <Loader2 className="h-4 w-4 animate-spin text-primary" />
        </div>
      )}
    </div>
  );
}
